/**
 * Pomodoro de focus pour l'évaluation active.
 *
 * @remarks
 * Découpé du legacy. Le tick (décrément des secondes) est géré par le
 * composant qui rend le timer, pas par le store — c'est ce qui simplifie
 * l'isolation.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { PomodoroState } from '../types';

interface PomodoroStoreState {
  pomodoro: PomodoroState;
  startPomodoro: (durationMinutes: number) => void;
  pausePomodoro: () => void;
  resumePomodoro: () => void;
  resetPomodoro: () => void;
  finishPomodoro: () => void;
}

const DEFAULT_POMODORO: PomodoroState = {
  status: 'idle',
  durationMinutes: 25,
  remainingSeconds: 25 * 60,
  startedAt: null,
  sessionsCompleted: 0,
  lastSessionDate: null,
};

export const usePomodoroStore = create<PomodoroStoreState>()(
  devtools(
    persist(
      (set) => ({
        pomodoro: { ...DEFAULT_POMODORO },

        startPomodoro: (durationMinutes) => {
          set({
            pomodoro: {
              status: 'running',
              durationMinutes,
              remainingSeconds: durationMinutes * 60,
              startedAt: Date.now(),
              sessionsCompleted: 0,
              lastSessionDate: null,
            },
          });
        },

        pausePomodoro: () => {
          set((state) => {
            const p = state.pomodoro;
            if (p.status !== 'running') return {};
            const elapsed = p.startedAt ? (Date.now() - p.startedAt) / 1000 : 0;
            const remaining = Math.max(0, p.remainingSeconds - elapsed);
            return {
              pomodoro: { ...p, status: 'paused', remainingSeconds: remaining, startedAt: null },
            };
          });
        },

        resumePomodoro: () => {
          set((state) => {
            const p = state.pomodoro;
            if (p.status !== 'paused') return {};
            return { pomodoro: { ...p, status: 'running', startedAt: Date.now() } };
          });
        },

        resetPomodoro: () => {
          set((state) => ({
            pomodoro: {
              ...DEFAULT_POMODORO,
              sessionsCompleted: state.pomodoro.sessionsCompleted,
              lastSessionDate: state.pomodoro.lastSessionDate,
            },
          }));
        },

        finishPomodoro: () => {
          set((state) => ({
            pomodoro: {
              ...state.pomodoro,
              status: 'finished',
              remainingSeconds: 0,
              startedAt: null,
              sessionsCompleted: state.pomodoro.sessionsCompleted + 1,
              lastSessionDate: new Date().toISOString().split('T')[0] ?? null,
            },
          }));
        },
      }),
      {
        name: 'saas-front-v2:tech-tests-pomodoro',
        version: 1,
        partialize: (state) => ({ pomodoro: state.pomodoro }),
        /**
         * Migration policy : si la shape de `PomodoroState` change (ex: ajout de
         * field obligatoire), bumper `version` et adapter. Payload corrompu →
         * `DEFAULT_POMODORO` (l'utilisateur perd la session locale mais le
         * timer redémarre proprement plutôt que de crasher).
         */
        // Aucune version antérieure n'existe : `migrate` n'est atteint que sur un
        // payload d'origine inconnue — on repart du pomodoro par défaut.
        // ⚠️ Au prochain bump, adapter AVANT ce retour en comparant avec `<` :
        // `if (oldVersion < 2) { …adapter… }`. Ne jamais tester
        // `oldVersion === <version courante>` : zustand n'appelle `migrate` que si
        // les versions diffèrent, la branche serait morte. Cascade de référence :
        // `use-layout-store`. (SAAS-903)
        migrate: () => ({ pomodoro: { ...DEFAULT_POMODORO } }),
      },
    ),
    { name: 'pomodoro', enabled: import.meta.env.DEV },
  ),
);
